import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import I18n from 'app/i18n';
import {observer} from 'mobx-react';
import Pagination from 'app/components/pagination';
import {betMoney, isGuest, scrollToTop} from 'app/utils';
import {type ParamsInterface} from 'app/utils/Get';
import {orderStore, userStore} from 'app/store/global';
import {marketplaceOrderController} from 'app/controllers/shop/marketplaceOrderController';
import Order from 'app/components/shop/marketplace/order';
import {showPopup} from 'app/utils/modals/ModalsService';
import PlaceByOrderArtifactPopup from './place_by_order_artifact_popup';
import {openAuth} from 'app/components/auth/auth';
import {translatedName} from 'app/utils/translations_helper';
import SimpleLineChart from 'app/components/my_profile/artifacts/simple_line_chart';
import PrizeCard from 'app/components/reusable/PrizeCard';
import {SimpleArtifactImage} from 'app/components/my_profile/artifacts/image';
import Smiles from 'app/components/my_profile/artifacts/popup/smiles';
import InventoryItemDescription from 'app/components/my_profile/artifacts/popup/inventory_item_description';
import {isLootBox} from 'app/utils/isLootBox';
import {locationQuery} from 'app/utils/navigate/location';
import GoBackButton from 'app/components/reusable/GoBackButton';
import MarketplaceOrderInterface from 'app/interfaces/MarketplaceOrderInterface';
import MarketplaceRecipeInterface from 'app/interfaces/MarketplaceRecipeInterface';
import PaginateInterface from 'app/interfaces/articles/PaginateInterface';
import RecipeInterface from 'app/interfaces/RecipeInterface';

const EMPTY_RECIPE: Partial<MarketplaceRecipeInterface> = {
    description: '',
    description_langs: {},
    langs: {},
    name: ''
};

interface RequestedArtifactsData {
    count?: number
    max_price?: number | string
}

interface MarketplaceOrdersResponse {
    orders?: MarketplaceOrderInterface[]
    paginate?: PaginateInterface
    recipe?: MarketplaceRecipeInterface
    requested_artifacts_data?: RequestedArtifactsData
}

const EMPTY_REQUESTED_ARTIFACTS_DATA: RequestedArtifactsData = {};

const marketplaceOrdersFromStore = (): MarketplaceOrdersResponse => orderStore as unknown as MarketplaceOrdersResponse;

const Orders = observer((): React.ReactElement => {
    const location = useLocation();
    const {name: marketplaceId} = useParams();
    const [loading, setLoading] = useState(true);

    const switchPage = useCallback(async(pageParams: ParamsInterface): Promise<void> => {
        setLoading(true);

        const loaded = await marketplaceOrderController.fetchOrders(marketplaceId, pageParams);

        setLoading(false);

        if (loaded) {
            scrollToTop();
        }
    }, [marketplaceId]);

    useEffect(() => {
        switchPage({page: locationQuery().page || 1});
    }, [location.pathname, switchPage]);

    const handlePlaceByOrderPopup = useCallback(() => {
        if (userStore.user.restrict_artifact) {
            return;
        }

        if (isGuest()) {
            openAuth(`/shop/marketplace/${marketplaceId}/orders`);
            return;
        }

        const {
            recipe,
            requested_artifacts_data = EMPTY_REQUESTED_ARTIFACTS_DATA
        } = marketplaceOrdersFromStore();

        const PlaceByOrderPopup = ({popupId}: {popupId: string}): React.ReactElement => <PlaceByOrderArtifactPopup
            popupId={popupId}
            recipe={recipe}
            max_price={requested_artifacts_data.max_price}
        />;

        showPopup('sell-inventory', PlaceByOrderPopup);
    }, [marketplaceId]);

    const {
        orders: items = [],
        recipe = EMPTY_RECIPE,
        paginate,
        requested_artifacts_data = EMPTY_REQUESTED_ARTIFACTS_DATA
    } = marketplaceOrdersFromStore();
    const {restrict_artifact} = userStore.user;
    const lootbox = isLootBox(recipe.item_type || '');
    const orderRecipe: RecipeInterface = {
        id: recipe.id || 0,
        langs: recipe.langs,
        name: recipe.name,
        slug: recipe.slug || marketplaceId || ''
    };

    return <div id="market-details" className="tabs__content-item">
        <div className="market-details">
            <div className="market-details--info">
                <div className="market-details--info--content">
                    <div className="market-details--top with--border">
                        <GoBackButton/>
                        <h1 className="market-details--info--desc-name">{translatedName(recipe)}</h1>
                    </div>
                    <div className="market-details--info--description">
                        <div className="market-details--info--img-wrap orders">
                            {lootbox
                                ? <div className="lootbox-detail__preview">
                                    <SimpleArtifactImage artifact={recipe}/>
                                </div>
                                : <div className="market-details--info--img market-item-card">
                                    <PrizeCard artifact={recipe} hideMeta hideName/>
                                </div>}
                        </div>
                        <div className="market-details--info--desc">
                            <div className="market-details--info--desc-title gray-title">
                                {I18n.t('overview')}
                            </div>
                            <div className="market-details--info--desc-text">
                                <InventoryItemDescription {...recipe}/>
                            </div>
                            <div className="market-details--info--slots">
                                <Smiles {...recipe}/>
                            </div>
                        </div>
                    </div>
                    <div className="market-details--info--prices">
                        <div className="market-details--info--prices-title">
                            {I18n.t('median_sale_price')}
                        </div>
                        <div className="market-details--info--prices-diagram">
                            <div id="inventory-sales">
                                <SimpleLineChart
                                    recipe={{
                                        id: marketplaceId,
                                        ...recipe
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="market-details--info--footer">
                    <div className="market-details__requests">
                        {Boolean(requested_artifacts_data.count) && <>
                            <div className="market-details__requests--count">{requested_artifacts_data.count}</div>
                            <div className="market-details__requests--goal">
                                <span>{I18n.t('requests_to_buy_at')}</span>
                            </div>
                            <div className="market-details__requests--price">
                                {betMoney({amount: requested_artifacts_data.max_price})}
                                <span>{I18n.t('or_lower')}</span>
                            </div>
                        </>}
                    </div>
                    <div className="market-details--info--footer-actions">
                        <button
                            disabled={loading}
                            className={`btn${restrict_artifact ? ' btn--disable' : ''}`}
                            onClick={handlePlaceByOrderPopup}
                        >
                            {I18n.t('place_buy_order')}
                        </button>
                    </div>
                </div>
            </div>
            <div className="market-details__items js-table">
                <div className="market-details__items__heading tables-heading">
                    <div className="market-details__items__name-seller">
                        <div className="market-details__items__name">{I18n.t('name')}</div>
                        <div className="market-details__items__seller">{I18n.t('seller')}</div>
                    </div>
                    <div className="market-details__items__price-actions">
                        <div className="market-details__items__price">{I18n.t('price')}</div>
                        <div className="market-details__items__actions"/>
                    </div>
                </div>
                {loading
                    ? <div className="market-details__item">
                        <div className="loader"/>
                    </div>
                    : items.map((order: MarketplaceOrderInterface) => <Order
                        key={order.id}
                        recipe={orderRecipe}
                        restrict_artifact={restrict_artifact}
                        query={{
                            marketplace_id: marketplaceId,
                            page: locationQuery().page
                        }}
                        {...order}
                    />)}
            </div>
            <Pagination {...paginate} switchPage={switchPage}/>
        </div>
    </div>;
});

export default Orders;
